import React from 'react';
import { Avatar, Box, Card, Chip, Typography } from '@mui/material';
import { Biotech as LabIcon, Business as OrgIcon, Groups as ConsortiumIcon, Place as PlaceIcon, Schedule as DeadlineIcon } from '@mui/icons-material';
import { markdownToText } from 'components/collab/lab/LabMarkdown';
import { EMPLOYMENT, KIND, WORK_MODE, daysLeft, fmtDeadline } from './positionLabels';

const HOST_ICON = { lab: LabIcon, consortium: ConsortiumIcon, organization: OrgIcon };

export default function PositionCard({ position: p, onNavigate }) {
  const Icon = HOST_ICON[p.host?.type] || OrgIcon;
  const left = daysLeft(p.deadline);
  const color = /^#[0-9a-f]{6}$/i.test(p.host?.color || '') ? p.host.color : '#0369a1';
  return (
    <Card onClick={() => onNavigate(`/positions/${p.id}/`)} sx={{
      height: '100%', minWidth: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', p: 1.75,
      borderTop: `3px solid ${color}`, transition: 'all 0.3s ease',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(0,0,0,0.12)' },
    }}>
      <Box display="flex" gap={1} alignItems="flex-start">
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontWeight: 600, fontSize: '1.15rem', lineHeight: 1.3, '&:hover': { textDecoration: 'underline' } }}>{p.title}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Icon sx={{ fontSize: 14 }} /> {p.host?.name}
            {p.host?.type !== 'organization' && p.organization_detail?.name && ` · ${p.organization_detail.name}`}
          </Typography>
        </Box>
        {p.host?.image && <Avatar variant="rounded" src={p.host.image} sx={{ width: 36, height: 36 }} />}
      </Box>
      <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
        <Chip size="small" label={KIND[p.kind] || p.kind} sx={{ height: 22, bgcolor: `${color}18`, color, fontWeight: 600 }} />
        <Chip size="small" variant="outlined" label={EMPLOYMENT[p.employment_type] || p.employment_type} sx={{ height: 22 }} />
        <Chip size="small" variant="outlined" label={WORK_MODE[p.work_mode] || p.work_mode} sx={{ height: 22 }} />
      </Box>
      {p.description && (
        <Typography variant="body2" color="text.secondary" sx={{
          mt: 1, fontSize: '0.8rem', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
        }}>
          {markdownToText(p.description)}
        </Typography>
      )}
      <Box flex={1} />
      <Box display="flex" alignItems="center" gap={1.5} mt={1.25} pt={1} sx={{ borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        {(p.location || p.organization_detail?.country) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PlaceIcon sx={{ fontSize: 14 }} /> {p.location || p.organization_detail.country}
          </Typography>
        )}
        {p.deadline && (
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: left !== null && left <= 7 ? 'warning.dark' : 'text.secondary' }}>
            <DeadlineIcon sx={{ fontSize: 14 }} /> {left !== null && left >= 0 ? `Apply by ${fmtDeadline(p.deadline)}` : `Closed ${fmtDeadline(p.deadline)}`}
          </Typography>
        )}
        <Box flex={1} />
        {p.applied && <Chip size="small" color="success" label="Applied" sx={{ height: 20 }} />}
        {p.can_manage && <Chip size="small" label={p.is_open ? 'Yours' : 'Yours · closed'} sx={{ height: 20 }} />}
      </Box>
    </Card>
  );
}
